import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatDate, useNavigateToLogout } from "../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DDLDataTable } from "../DDLDataTable";
import { EnhancedButtonPrimeReact } from "../EnhancedButtonPrimeReact";
import { IAppState } from "../../store/reducers";
import { IGetListRequest } from "../../models";
import { IWSIBClass } from "../../models/WSIBClass";
import { WSIBClassRateInfoSetupFieldsPanel } from "./Panels/WSIBClassRateSetupFieldsPanel";
import { WSIBClassRateService } from "../../services/WSIBClassRateService";
import { WSIBClassRateSetupToolbar } from "./WSIBClassRateSetupToolbar";
import { WSIBClassRateSetupValidationSchema } from "./validator";
import { WSIBClassService } from "../../services/WSIBClassService";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions";
import { yupResolver } from "@hookform/resolvers/yup";

// import { SingleColumnLayout } from "../EmployeeAssignemnt/SingleColumnLayout";




export interface IWSIBClassRateSetup { }
const SINGLE_RECORD_DATA_QUERY_KEY = "wsibClass-single-data-record";
const WSIB_CLASS_LIST_DATA_QUERY_KEY = "wsibClass-list";

const useGetData = (
	clientId: string,
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, clientId, id, navigateToLogout],
		queryFn: () => {
			return WSIBClassRateService.getById(clientId, id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const useWSIBClassSetupDDL = (
	provinceId: string
) =>
	useQuery({
		queryKey: ["wsib-class-ddl", provinceId],
		queryFn: (data) => {

			return WSIBClassService.getGetWSIBClassDDLByProvinceId(provinceId);
		},
		refetchOnWindowFocus: false,
	});

const useWSIBClassRateList = (
	provinceId: string
) =>
	useQuery({
		queryKey: [WSIB_CLASS_LIST_DATA_QUERY_KEY],
		queryFn: (data) => {

			return WSIBClassRateService.getWSIBClassRateList(
				provinceId
			);
		},
		refetchOnWindowFocus: false,
	});


export const WSIBClassRateSetup: React.FC<IWSIBClassRateSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [wsibClassRateId, setWSIBClassRateId] = useState<number | string>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [provinceId, setProvinceId] = useState<any>();
	const queryClient = useQueryClient();

	const [isLoading, setIsLoading] = useState(false);
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState(id);

	useEffect(() => {
		if (id) {
			setEditRecordId(id)
		}
	}, [id])

	// const [runGetListAPICall, setRunGetListAPICall] =
	// 	useState<any>(true);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "label",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "label",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
		// setRunGetListAPICall(true)
	}, []);

	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		clientId,
		editRecordId,
		navigateToLogout
	);

	const { isPending: isClassDDLLoading, data: wsibClassSetupDDL } = useWSIBClassSetupDDL(provinceId);
	const { isPending: isClassRateLoading, data: wsibClassRateList } = useWSIBClassRateList(provinceId);

	const DEFAULT_VALUES = {
		rate: undefined,
		effectiveDate: null,
		endDate: null,
	};

	const formMethods = useForm<any>({
		resolver: yupResolver(WSIBClassRateSetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES
	});

	const { isPending: isSubmitting, mutate: submitWSIBClassRateFormFields } =
		useMutation({
			mutationFn: WSIBClassRateService.create,
			onSuccess: (res: IWSIBClass | IServerError | IResponse | any) => {

				if (res.status === 200) {
					// if (instanceOfIWSIBClassResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/workplace-safety/class-rate/setup/${res.data.id}`;
					navigate(path);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [WSIB_CLASS_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else if (res.status === 201) {
					// if (instanceOfIWSIBClassResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/workplace-safety/class-rate/setup/${res.data.id}`;
					navigate(path);
					setEditRecordId(res.data.id);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [WSIB_CLASS_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const upadtedWSIBClassRateListItems = useMemo(() => {
		return (
			wsibClassRateList &&
			wsibClassRateList.items?.map((item: any) => ({
				...item,
				label: (
					<Link
						to={`/workplace-safety/class-rate/setup/${item.id}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light }}
						onClick={() => setEditRecordId(item.id)}
					>
						{item.rate}
					</Link>
				),

			}))
		);
	}, [wsibClassRateList]);



	const onSubmit = (values: any) => {
		let payload = {
			provinceId: values.provinceId,
			classId: values.classId,
			rate: values.rate,
			effectiveDate: formatDate(values.effectiveDate),
			endDate: formatDate(values.endDate),
		};
		submitWSIBClassRateFormFields(payload);
	};

	useEffect(() => {
		if (id && selectedDataRecord) {
			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				if (fieldName === "effectiveDate" && fieldValue) {
					let date = new Date(fieldValue)
					formMethods.setValue(
						fieldName,
						date
					);
				}
				if (fieldName === "endDate" && fieldValue) {
					let date = new Date(fieldValue)
					formMethods.setValue(
						fieldName,
						date
					);
				}
				if (fieldName === "provinceId") {
					setProvinceId(fieldValue)
				}
				// if (fieldName === "classId" && fieldValue) {
				// 	(fieldName)
				// }

			});
		}
	}, [id, selectedDataRecord]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteWSIBClassRateById } = useMutation({
		mutationFn: WSIBClassRateService.delWSIBClassRate,

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {
				// remove the cache of the WSIBClass fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/workplace-safety/class-rate/setup`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [WSIB_CLASS_LIST_DATA_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const deleteWSIBClassField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				wsibClassRateId: wsibClassRateId,
				navigateToLogout: navigateToLogout,
			};
			deleteWSIBClassRateById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setWSIBClassRateId(selectedDataRecord.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setWSIBClassRateId(params.id);
	}, []);

	const { isPending: isArchive, mutate: archiveWSIBClassById } = useMutation({
		mutationFn: WSIBClassService.archiveWSIBClass,

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {
				// remove the cache of the WSIBClass fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/workplace-safety/class-rate/setup`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [WSIB_CLASS_LIST_DATA_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const archiveWSIBClassField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				wsibClassRateId: wsibClassRateId,
				navigateToLogout: navigateToLogout,
			};
			archiveWSIBClassById(ArchivePayload);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (selectedDataRecord) {
			setWSIBClassRateId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		formMethods.setValue('provinceId', undefined)
		let path = `/workplace-safety/class-rate/setup`;
		navigate(path);
	};

	const onProvinceSelectionChangeHandler = (e: any) => {
		setProvinceId(e.value)
	}

	const expandedLabels = ["Workplace Safety", "Class"];
	const expandedWSIBClassRateSetupOrSetupItem = "Rate Setup"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Class" &&
				item.label === expandedWSIBClassRateSetupOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: parentLabel === "Class" && item.label === expandedWSIBClassRateSetupOrSetupItem && "/workplace-safety/class-rate/setup",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		// dispatch(
		// 	setSelectedActivityLogActions({
		// 		entityId1: "",
		// 		entityId2: "",
		// 		entityId3: "",
		// 		screenName: ACCOUNTANT_SETUP_SCREEN_SLUG,
		// 		wsibClassName: "",
		// 		activityType:
		// 			"activity_type=create&activity_type=updated&activity_type=delete",
		// 		activityLogVisibility: true,
		// 	})
		// );
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	const onRowEditClicked = useCallback((params: IWSIBClass | any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/workplace-safety/class-rate/setup/${params.value}`;
		navigate(path);
	}, []);

	return (
		<Grid
		// sx={{
		// 	pb: "5rem",
		// 	"& .MuiAppBar-root": {
		// 		zIndex: 2,
		// 		top: subMenuVisibility ? "154px" : "112px",
		// 	},
		// }}
		>
			<BackdropCircularProgress
				open={isSubmitting || isPendingById || isDeleting || isLoading || isArchive || isClassRateLoading || isClassDDLLoading}
				testId="comapny Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteWSIBClassField}
				cancelActionHandler={onCloseHandler}
				testId="wsibClass-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="WSIBClass"
										testId={`wsibClass`}
										// leftSpacing={1}
										// rightSpacing={1}
										showTitle={false}
										variant="outlined"
										topPadding={2}
									>
										<WSIBClassRateInfoSetupFieldsPanel
											wsibClassSetupDDL={wsibClassSetupDDL && wsibClassSetupDDL}
											onProvinceSelectionChangeHandler={onProvinceSelectionChangeHandler}
											setIsLoading={(value: boolean) => setIsLoading(value)}
										// wsibClassRateList={wsibClassRateList}
										/>
									</StandardPanel>
								}
								toolbar={
									<WSIBClassRateSetupToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
									/>
								}
								showFullDiaplay={true}
								toolbarPosition="bottom"
								testId="form-layout"
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 5, lg: 5, xl: 5 }}></Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}
							sx={{ marginTop: "16px" }}
						>
							<DDLDataTable
								rows={
									wsibClassRateList && upadtedWSIBClassRateListItems
										? upadtedWSIBClassRateListItems
										: []
								}
								columns={[{ field: "label", headerName: "Rate" }, { field: "class", headerName: "Class" }]}
								showHeaderColumns={true}
								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								onRowArchiveClicked={onArchiveClicked}
								showArchiveIcon={false}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
								showPaginator={false}
								selectedId={selectedDataRecord && id && selectedDataRecord.id}
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			{/* Archive Dialogue */}
			<EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseArchiveHandler}
				open={openArchiveDialog}
				dialogContent={null}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseArchiveHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Archive"
							severity="secondary"
							raised
							onClickHandler={() => archiveWSIBClassField}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"wsibClass-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
